//imports
import React, { useEffect, Fragment, useCallback, useState, useRef } from 'react';
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from 'react-redux';
import { useRealtimeDrivers } from 'Core/hooks';

import { NotificationManager } from 'Components/Notifications';
import { HasPermission } from "Core/security"
import { GetFormatForMoment } from 'Core/data/Helpers';
import { Timezone } from 'Components/uiControls'
import moment from 'moment';

import {
    TableView,
    TableButtons,
    Button,
    IconButton,
    SearchInput,
    MenuComponent,
    Icons,
    Tooltip,
    Pill
} from '@gpstrackit/gtc-react-controls';

import { Grid, Typography } from "@mui/material";
import AdvancedFilters from './components/advancedFilters';

//Map
import MapModal from 'Modules/.common/components/MapModal';
import ViewAnswers from '../../../../modals/viewAnswers';

import {
    sendGenericExport,
    clearRedux
} from "Redux/actions";

import {
    reset,
    searchDynamicFormAnswers,
    updateDynamicFormAnswersFilters,
    openAdvancedFilters,
    closeAdvancedFilters
} from './redux/slice';

import {
    openViewAnswersModal
} from '../../../../modals/viewAnswers/redux/slice';

//Hook component
const DynamicFormAnswersList = (props) => {
    const { messages, locale } = useIntl();
    const dispatch = useDispatch();

    const tableRef = useRef();
    //Redux
    const exportResult = useSelector(state => state.commomRedux.exportResult);
    const exportName = useSelector(state => state.commomRedux.exportName);

    const drivers = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });

    const filters = useSelector(state => state.dynamicFormAnswersRedux.filters);
    const openAdvancedFiltersModal = useSelector(state => state.dynamicFormAnswersRedux.openAdvancedFilters);
    const loading = useSelector(state => state.dynamicFormAnswersRedux.loading);
    const items = useSelector(state => state.dynamicFormAnswersRedux.items);
    const nextToken = useSelector(state => state.dynamicFormAnswersRedux.nextToken);
    const error = useSelector(state => state.dynamicFormAnswersRedux.error);

    const user = useSelector((state) => state.securityRedux.user);

    const openViewAnswers = useSelector(state => state.dynamicFormsViewAnswersRedux.open);

    const { dateformat, timeZone } = user;
    const format = GetFormatForMoment(dateformat);

    const options = [
        messages['dynamicFormsMain_viewAnswers'],
    ];

    const [columns, setColumns] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [answer, setAnswer] = useState(null);

    useEffect(() => {
        if (!filters?.avoidFirstLoad) {
            dispatch(searchDynamicFormAnswers(filters));
        } else {
            dispatch(updateDynamicFormAnswersFilters({ ...filters, avoidFirstLoad: false }));
        }

        return () => dispatch(reset());
    }, []);

    useEffect(() => {
        setColumns([
            {
                key: 'form',
                title: messages['dynamicFormAnswers_form'],
                show: true,
                export: true,
                render: (row) => {
                    return (
                        <Grid
                            container
                            alignItems="center"
                            justifyContent="space-between"
                            className="context-menu-hover-container"
                            wrap="nowrap"
                        >
                            <Grid item xs zeroMinWidth style={{ width: 150 }}>
                                <Tooltip placement='top' title={row?.dynamicForm?.name}>
                                    <Typography
                                        noWrap
                                        className='btn-link'
                                        onClick={() => {
                                            dispatch(openViewAnswersModal(row?.id))
                                        }}
                                    >
                                        {row?.dynamicForm?.name}
                                    </Typography>
                                </Tooltip>
                            </Grid>
                            <Grid item justifyContent="flex-end">
                                <MenuComponent
                                    disabled={loading}
                                    options={options}
                                    placement="right"
                                    icon={'more_vert'}
                                    className="context-menu-hover"
                                    onClick={(type) => {
                                        if (type === messages['dynamicFormsMain_viewAnswers']) {
                                            dispatch(openViewAnswersModal(row?.id))
                                        }
                                    }}
                                    value={row}
                                />
                            </Grid>
                        </Grid>
                    )
                },
                functionString: 'return row?.dynamicForm?.name || ""'
            },
            // {
            //     key: 'answerName',
            //     title: messages['dynamicFormAnswers_answer'],
            //     show: true,
            //     export: true,
            //     render: (row) => {
            //         return row?.answerName || "";
            //     },
            //     functionString: `return row?.answerName`
            // },
            {
                key: 'updatedBy',
                title: messages['dynamicFormAnswers_updatedBy'],
                show: true,
                export: true,
                render: (row) => {
                    const driver = drivers?.find(d => `DRIVER_${d.driverId}` == row?.updatedBy);
                    return driver?.name || "";
                },
                functionString: `return row?.updatedBy`
            },
            {
                key: 'address',
                title: messages['dynamicFormAnswers_address'],
                show: true,
                export: true,
                render: (row) => {
                    return (
                        <Grid
                            container
                            direction="row"
                            justifyContent="flex-start"
                            alignItems="center"
                            spacing={1}
                        >
                            <Grid item>
                                <Icons name="room" />
                            </Grid>
                            <Grid item xs zeroMinWidth>
                                <Typography
                                    noWrap
                                    className="btn-link-strong"
                                    onClick={() => setAnswer(row)}
                                >
                                    {row?.coordinates?.address}
                                </Typography>
                            </Grid>
                        </Grid>
                    )
                },
                functionString: `return row?.coordinates?.address ||  ""`
            },
            {
                key: 'updatedOn',
                title: messages['dynamicFormAnswers_updatedAt'],
                show: true,
                export: true,
                render: (row) => {
                    return (
                        <Timezone
                            date={row?.updatedOn}
                            dateFormat={dateformat}
                            timeFormat="hh:mm a"
                            timezone={timeZone}
                        />
                    )
                },
                functionString: `return row?.updatedOn ? moment(row?.updatedOn).format('${format}, hh:mm a') :  ""`
            }
        ]);
    }, [locale, drivers]);

    useEffect(() => {
        if (error) {
            NotificationManager.error(
                messages['errorMessage'],
                messages['error'],
                3000,
                null,
                null,
                'error',
            );
            dispatch(reset({ error: null }));
        }
    }, [error]);

    useEffect(() => {
        if (exportResult && exportName === "dynamicFormAnswers") {
            NotificationManager.info(
                messages['exportInProcessBody'],
                messages['exportInProcessTitle'],
                4000,
                null,
                null,
                'info',
                null
            );
            dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
        }
    }, [exportResult, exportName]);

    const onChangePage = useCallback((newNexToken, newSearchText, resetSearch) => {
        dispatch(searchDynamicFormAnswers({
            ...filters,
            openSearch: newSearchText || "",
            resetSearch: resetSearch || false,
            limit: filters?.limit,
            nextToken: newNexToken
        }));
    });

    const onSearchText = useCallback((newSearchText) => {
        setSearchText(newSearchText);

        if (window.typingTime) {
            clearTimeout(window.typingTime);
        }

        window.typingTime = setTimeout(() => {
            tableRef?.current?.setSelectedItems([]);
            setSelectedItems([]);
            onChangePage(null, newSearchText, true);
            window.typingTime = null;
        }, 500);
    });

    const resetTable = () => {
        setSelectedItems([]);
        tableRef?.current?.setSelectedItems([]);
        setSearchText("");
    }

    let pillTimeLabel = "";
    if (filters?.dateRange?.start && filters?.dateRange?.end) {
        pillTimeLabel = `${messages[`dynamicFormAnswers_${filters?.timeFrame}`] ? messages[`dynamicFormAnswers_${filters?.timeFrame}`] : ""} (${moment(filters?.dateRange?.start).format(format)} - ${moment(filters?.dateRange?.end).format(format)})`;
    }

    return (
        <div>
            <Grid container direction="row" justifyContent="space-between" alignItems="center">
                <Grid item xs={6} sm={2}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item>
                            <IconButton
                                tooltip={messages["filters"]}
                                placement="bottom"
                                variant="secondary"
                                icon="filter_list"
                                size="small"
                                disabled={loading}
                                onClick={() => {
                                    if (!openAdvancedFiltersModal) {
                                        dispatch(openAdvancedFilters())
                                    } else {
                                        dispatch(closeAdvancedFilters())
                                    }
                                }}
                            />
                        </Grid>

                        {
                            (selectedItems.length > 0) &&
                            <Grid item>
                                <IconButton
                                    icon="delete"
                                    placement="bottom"
                                    variant="secondary"
                                    size="small"
                                    tooltip={messages['delete']}
                                    disabled={loading}
                                    onClick={() => {
                                        //dispatch(openDeleteModal(selectedItems));
                                    }}
                                />
                            </Grid>
                        }
                    </Grid>
                </Grid>

                <Grid item>
                    <Pill
                        textField={"label"}
                        item={{ id: 1, label: pillTimeLabel, color: "#005bf8" }}
                        variant='filled'
                        disabled={loading}
                        onClick={() => {
                            if (!openAdvancedFiltersModal) {
                                dispatch(openAdvancedFilters())
                            } else {
                                dispatch(closeAdvancedFilters())
                            }
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={4} className="mb-10 text-right">
                    <SearchInput
                        placeholder={messages['dynamicFormAnswers_searchByForm']}
                        searchText={searchText}
                        onSearchText={onSearchText}
                    />
                </Grid>
            </Grid>

            <TableView
                ref={tableRef}
                keyColumn="id"
                data={items || []}
                columns={columns || []}
                infiniteScroll={true}
                inputSearch={false}
                moreRowsAvailable={nextToken}
                nextToken={nextToken}
                onChangePage={() => {
                    onChangePage(nextToken, searchText);
                }}
                onSelectedItems={(newSelectedItems) => {
                    setSelectedItems(newSelectedItems || []);
                }}
                noMoreItemsMessage={messages['noMoreItems']}
                hi
                loading={loading}
                topheight={236}
                hideDragable
                searchText={searchText}
                disableItemSelection
            />

            <AdvancedFilters resetTable={resetTable} />

            {
                answer &&
                <MapModal
                    title={`${answer?.coordinates?.address}`}
                    hiddenButton
                    show={answer ? true : false}
                    pointers={[
                        {
                            id: answer?.id,
                            latitude: parseFloat(answer?.coordinates?.latitude || 0),
                            longitude: parseFloat(answer?.coordinates?.longitude || 0),
                            direction: 35,
                            heading: '1',
                            Label: answer?.formId,
                            eventName: "Stop"
                        }
                    ]}
                    hiddenTraffic={true}
                    hiddenWeather={true}
                    hiddenLegends={true}
                    hiddenSearchMap={true}
                    hideModal={() => setAnswer(null)}
                    zoom={18}
                />
            }

            {
                openViewAnswers &&
                <ViewAnswers />
            }
        </div>
    );
}

export default DynamicFormAnswersList;